import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@ticketon/authentication-core";
import {
  MarketingWorkspaceProvider as SharedMarketingWorkspaceProvider,
  type IMarketingWorkspaceConfig,
} from "@ticketon/marketing/workspace";
import { getMarketingAdminClient } from "@/graphql/marketingAdminClient";
import { usePermissions } from "@/hooks/usePermissions";
import { MARKETING_ASSET_BROWSE_PERMISSIONS } from "./permissions";

/**
 * Mounts the shared marketing workspace state in "single" mode: the travel admin never creates
 * workspaces, it only joins the ones the user already belongs to. The first membership is active
 * by default and the asset library offers a picker to change it; a user with no membership sees
 * an empty state until a workspace admin invites them. Users without any Marketing: grant never
 * load workspaces at all - for them the library simply does not exist.
 */
export function MarketingWorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { i18n } = useTranslation();
  const { hasAnyPermission, isLoading } = usePermissions();

  const mayUseAssets = !isLoading && hasAnyPermission(MARKETING_ASSET_BROWSE_PERMISSIONS);

  const config: IMarketingWorkspaceConfig = {
    getClient: getMarketingAdminClient,
    mode: "single",
    plansEnabled: false,
    locale: i18n.language,
    currentUserEmail: user?.email,
  };

  return (
    <SharedMarketingWorkspaceProvider config={config} isAuthenticated={isAuthenticated && mayUseAssets}>
      {children}
    </SharedMarketingWorkspaceProvider>
  );
}
